import type {
  HomeySnapshot,
  HomeyServerMessage,
  HomeyDeviceSnap,
  HomeyZoneSnap,
  PresencePersonVM,
} from "@/types/homey";

/**
 * Pure, immutable snapshot patcher.
 *
 * Applies a single HomeyServerMessage delta to a HomeySnapshot and returns a
 * new snapshot. The original is never mutated. For messages that carry no
 * state change (hello, homey.status) the SAME reference is returned so React
 * Query consumers do not re-render.
 */
export function applyServerMessage(
  snapshot: HomeySnapshot,
  msg: HomeyServerMessage,
): HomeySnapshot {
  switch (msg.type) {
    case "hello":
    case "homey.status":
      // No state to patch — return identity so downstream memo/selectors stay stable.
      return snapshot;

    case "device":
      return { ...snapshot, devices: upsertById(snapshot.devices, msg.device, "id") };

    case "device.delete":
      return { ...snapshot, devices: snapshot.devices.filter((d) => d.id !== msg.deviceId) };

    case "zone":
      return { ...snapshot, zones: upsertById(snapshot.zones, msg.zone, "id") };

    case "zone.delete":
      return { ...snapshot, zones: snapshot.zones.filter((z) => z.id !== msg.zoneId) };

    case "presence":
      return { ...snapshot, presence: upsertPresence(snapshot.presence, msg.person) };

    case "presence.delete":
      return {
        ...snapshot,
        presence: snapshot.presence.filter((p) => p.id !== msg.personId),
      };
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Immutably upserts `item` into `arr` (replace by `key` if present, append if
 * not), then sorts the result by `name` ascending.
 */
function upsertById<T extends HomeyDeviceSnap | HomeyZoneSnap>(
  arr: T[],
  item: T,
  key: keyof T,
): T[] {
  const exists = arr.some((el) => el[key] === item[key]);
  const updated = exists ? arr.map((el) => (el[key] === item[key] ? item : el)) : [...arr, item];
  return [...updated].sort((a, b) =>
    (a.name as string).localeCompare(b.name as string, undefined, { sensitivity: "base" }),
  );
}

/**
 * Immutably upserts a person into the presence array by id, then sorts
 * present-first (then by name) to mirror the server's mapPresence ordering.
 */
function upsertPresence(arr: PresencePersonVM[], item: PresencePersonVM): PresencePersonVM[] {
  const exists = arr.some((p) => p.id === item.id);
  const updated = exists ? arr.map((p) => (p.id === item.id ? item : p)) : [...arr, item];
  return [...updated].sort(
    (a, b) =>
      Number(b.present) - Number(a.present) ||
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
  );
}
